'use client';

import React from 'react';
import { useLanguage } from '@/context/LanguageContext';

export default function HeroSection() {
  const { t } = useLanguage();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-[92vh] flex items-center justify-center pt-32 pb-20 px-6 overflow-hidden bg-[#050507]"
      aria-label="Hero"
    >
      {/* Ambient background glows */}
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-purple-brand/25 via-magenta-brand/15 to-gold/15 rounded-full blur-[140px] pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-10 right-10 w-96 h-96 bg-gold/10 rounded-full blur-[120px] pointer-events-none"
        aria-hidden="true"
      />

      {/* Subtle tech grid background */}
      <div
        className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_40%,#000_70%,transparent_100%)] pointer-events-none"
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto w-full relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Left Column: Copy & Value Proposition */}
        <div className="lg:col-span-7 space-y-8 text-center lg:text-left">
          {/* Top Badge */}
          <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-gold/30 bg-gradient-to-r from-gold/10 via-purple-brand/10 to-transparent backdrop-blur-md">
            <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
            <span className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-gold-light">
              {t.hero.badge}
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-white leading-[1.1] uppercase">
            {t.hero.headlinePart1}{' '}
            <span className="bg-gradient-to-r from-gold via-gold-light to-amber-400 bg-clip-text text-transparent">
              {t.hero.headlineHighlight}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-base sm:text-lg text-slate-300 font-normal leading-relaxed max-w-2xl mx-auto lg:mx-0">
            {t.hero.sub}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
            <button
              onClick={() => scrollTo('contacte')}
              className="w-full sm:w-auto px-8 py-4 rounded-xl text-xs sm:text-sm font-black uppercase tracking-wider text-black bg-gradient-to-r from-gold via-gold-light to-gold-dark hover:brightness-110 shadow-xl shadow-gold/25 hover:shadow-gold/40 transform hover:-translate-y-0.5 transition-all duration-200"
            >
              {t.hero.ctaPrimary}
            </button>
            <button
              onClick={() => scrollTo('solucions')}
              className="w-full sm:w-auto px-7 py-4 rounded-xl text-xs sm:text-sm font-bold uppercase tracking-wider text-white border border-white/15 bg-white/5 hover:bg-white/10 hover:border-gold/50 backdrop-blur-md transition-all duration-200"
            >
              {t.hero.ctaSecondary}
            </button>
          </div>

          {/* 3 Pills */}
          <div className="pt-4 flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-6 text-xs text-slate-400">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gold flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>{t.hero.pill1}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gold flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>{t.hero.pill2}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gold flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>{t.hero.pill3}</span>
            </div>
          </div>
        </div>

        {/* Right Column: Interactive Visual Showcase */}
        <div className="lg:col-span-5 relative">
          <div className="relative mx-auto max-w-md w-full">
            {/* Device Frame */}
            <div className="relative rounded-3xl p-3 sm:p-4 bg-gradient-to-b from-white/10 to-white/5 border border-gold/30 shadow-2xl shadow-purple-brand/20 backdrop-blur-xl">
              {/* Inner Mockup Card */}
              <div className="rounded-2xl bg-[#0d0d14] border border-white/10 p-5 space-y-4 text-white overflow-hidden">
                {/* Browser/Device Header */}
                <div className="flex items-center justify-between pb-3 border-b border-white/10 text-xs text-slate-400">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                  </div>
                  <span className="text-[11px] font-mono text-slate-400 bg-white/5 px-2.5 py-0.5 rounded-full border border-white/5">
                    elteunegoci.com
                  </span>
                  <span className="w-4" />
                </div>

                {/* Business Hero in Mockup */}
                <div className="rounded-xl p-4 bg-gradient-to-br from-white/5 via-[#131320] to-purple-brand/20 border border-gold/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-gold uppercase tracking-wider">
                      {t.hero.mockup.category}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full font-bold">
                      {t.hero.mockup.status}
                    </span>
                  </div>
                  <h3 className="text-base font-extrabold text-white leading-snug">
                    {t.hero.mockup.title}
                  </h3>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    {t.hero.mockup.description}
                  </p>

                  {/* Actions in Mockup */}
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <a
                      href="https://wa.me/34694919757?text=Hola%2C%20m%27agradaria%20veure%20una%20demo%20de%20la%20web"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-md transition-colors"
                    >
                      <span>{t.hero.mockup.ctaPrimary}</span>
                    </a>
                    <button
                      onClick={() => scrollTo('solucions')}
                      className="py-2 px-3 rounded-lg bg-white/10 hover:bg-white/15 text-white font-medium text-xs text-center border border-white/10 transition-colors"
                    >
                      {t.hero.mockup.ctaSecondary}
                    </button>
                  </div>
                </div>

                {/* Local Google Maps & Ratings Card */}
                <div className="rounded-xl p-3 bg-white/5 border border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 text-sm font-black">
                      ★
                    </div>
                    <div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-bold text-white">4.9 / 5.0</span>
                        <span className="text-[10px] text-slate-400">{t.hero.mockup.reviewsText}</span>
                      </div>
                      <span className="text-[10px] text-gold font-medium">
                        {t.hero.mockup.locationText}
                      </span>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-slate-300 bg-white/5 px-2 py-1 rounded">
                    Google Maps
                  </span>
                </div>

                {/* AI & Structured Data Feature Badge */}
                <div className="rounded-xl p-3 bg-gradient-to-r from-purple-brand/30 to-magenta-brand/10 border border-purple-brand/40 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-magenta-brand/20 flex items-center justify-center text-magenta-brand font-bold text-xs flex-shrink-0">
                    IA
                  </div>
                  <div className="text-[11px] text-slate-200">
                    <strong className="text-white">{t.hero.mockup.aiTitle}</strong>: {t.hero.mockup.aiText}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating conversion tag */}
            <div className="absolute -bottom-4 -left-4 sm:-bottom-5 sm:-left-6 bg-[#0E0E14] border border-gold/40 rounded-2xl px-4 py-2.5 shadow-2xl shadow-black flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-emerald-400 animate-ping" />
              <div className="text-left">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gold">{t.hero.mockup.floatingTag}</p>
                <p className="text-xs font-extrabold text-white">{t.hero.mockup.floatingPrice}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
